<template>
  <div class="god_page_container">
    <div class="page-title-row">
      <h2>Gestión de Cupones</h2>
      <button @click="addCoupon()" class="main_button-green">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <line x1="12" y1="5" x2="12" y2="19"></line>
          <line x1="5" y1="12" x2="19" y2="12"></line>
        </svg>
        Añadir cupón
      </button>
    </div>

    <div id="selector_container">
      <input
        type="email"
        class="input_200"
        v-model="coupon"
        placeholder="Buscar por código de cupón"
        @keyup.enter="getCoupons()"
      />

      <label class="checkbox-filter">
        <input type="checkbox" name="aceptar" v-model="showExpired">
        <span>Mostrar caducados</span>
      </label>

      <button @click="getCoupons()" class="main_button" style="min-width: 140px;">
        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="11" cy="11" r="8"></circle>
          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
        </svg>
        Buscar
      </button>
    </div>
    
    <div class="table-container">
      <table>
        <thead>
          <tr>
              <th class="small">Código</th>
              <th class="small">Descuento</th>
              <th class="small">Usos</th>
              <th class="small">Caducidad</th>
              <th class="small">Plataforma</th>
              <th class="small">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr 
            v-for="(item, index) in coupons"
            :key="item.codigo"
            :class="index % 2 === 0 ? 'bg-gray-100' : 'bg-white'"
          >
            <td>
              <span v-if="!item.activo" class="badge badge-danger">🚫 {{ item.codigo }}</span>
              <strong v-else>{{ item.codigo }}</strong>
            </td>
            <td><strong>{{ item.valor_descuento }} {{ item.tipo_descuento == "euros" ? "€" : "%" }}</strong></td>
            <td>{{ item.usos }} <span class="text-subtle">({{ item.usos_maximo == "0" ? "sin límite" : (item.usos_maximo + " max") }})</span></td>
            <td>{{ item.fecha_caducidad }}</td>
            <td>
              <span class="badge badge-neutral">{{ obtenerPlafaforma(item.platform) }}</span>
            </td>
            <td>
              <div class="actions-cell">
                <img class="action_icon" title="Editar" src="@/assets/images/ic_action_edit.png" @click="onEditClicked(item)"/>
                <img class="action_icon" title="Eliminar" src="@/assets/images/ic_action_delete.png" @click="onDeleteClicked(item)"/>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <CouponDetailsPopup
      :show-coupon-popup="showCouponPopup"
      :coupon="selectedCoupon"
      @couponSaved="onCouponSaved"
      @couponPopupCancelled="couponPopupCancelled"
      @errorHappened="errorHappened"
      />

    <ErrorPopup :errorMessage="error" />
    <TwoButtonPopup
      :title="twoButtonTitle"
      :message="twoButtonMessage"
      :acceptButton="twoButtonAcceptButton"
      @acceptCallback="() => twoButtonConfirmed()"
      :cancelButton="twoButtonCancelButton"
      @cancelCallback="() => twoButtonCancelled()"
      />

  </div>

</template>

<script setup>
  import { ref, nextTick, watch } from 'vue'
  import { useLoadingStore } from "@/stores/loadingStore";
  import { refreshToken } from "@/services/authService";
  import { useRouter, useRoute } from 'vue-router';
  import ErrorPopup from "@/components/common/ErrorPopup.vue";
  import TwoButtonPopup from "@/components/common/TwoButtonPopup.vue";
  import CouponDetailsPopup from "@/components/coupons/CouponDetailsPopup.vue";

  const showCouponPopup = ref(false);
  const selectedCoupon = ref(null);

  const error = ref(null);
  const coupons = ref([]);
  const loadingStore = useLoadingStore();

  const router = useRouter();
  const route = useRoute();

  const showExpired = ref(route.query.showExpired === 'true');
  const coupon = ref(route.query.coupon || '');

  watch([showExpired, coupon], () => {
    router.replace({
      query: {
        ...route.query,
        showExpired: showExpired.value,
        coupon: coupon.value
      }
    });
  });

  const twoButtonTitle = ref("");
  const twoButtonMessage = ref("");
  const twoButtonAcceptButton = ref("");
  const twoButtonCancelButton = ref("");
  let twoButtonConfirmed = null;
  let twoButtonCancelled = null;

  function obtenerPlafaforma(platform) {
    switch (platform) {
      case 'app':
        return 'App';
      case 'web':
        return 'Web';
      default:
        return 'Todas';
    }
  }

  const getCoupons = async () => {
    loadingStore.setLoading(true);
    error.value = null;

    try {
      let response = await callAPIToGetCoupons();

      if (response.status === 401) {
        const refreshed = await refreshToken();
        if (refreshed) {
          response = await callAPIToGetCoupons();
        } else {
          loadingStore.setLoading(false);
          localStorage.removeItem('user_id');
          localStorage.removeItem('token');
          router.push('/');
          return;
        }
      }

      if (!response.ok) {
        showError("Se ha producido un error obteniendo los cupones.");
        return;
      }

      const data = await response.json();

      if (data.errors) {
        showError(data.message);
        coupons.value = [];
      } else {
        coupons.value = data.coupons;
      }
    } catch (exception) {
      showError(exception.message);
    } finally {
      loadingStore.setLoading(false);
    }
  }

  async function callAPIToGetCoupons() {
    return await fetch(process.env.VUE_APP_API_URL+"api/god/coupons/get?user_id="+localStorage.getItem('user_id')
      +"&show_expired="+(showExpired.value ? "1" : "0")
      + (coupon.value != '' ? "&coupon="+coupon.value : ""),
       {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "vgod": process.env.VUE_APP_API_VGOD,
          "cp-token": process.env.VUE_APP_API_CP_TOKEN,
          "Authorization": "Bearer " + localStorage.getItem('token')
        },
      });
  }

  const onEditClicked = async (item) => {
    if (!item.codigo.includes(" (Amigo)")) {
      selectedCoupon.value = item;
      showCouponPopup.value = true;
    } else {
      showError("No se pueden editar los cupones de amigo.");
    }
  };

  const onDeleteClicked = async (item) => {
    twoButtonTitle.value = item.activo ? "Desactivar cupón" : "Activar cupón";
    twoButtonMessage.value = item.activo ? "¿Estás seguro de que deseas desactivar este cupón?" : "¿Estás seguro de que deseas activar este cupón?";
    twoButtonAcceptButton.value = "Aceptar";
    twoButtonCancelButton.value = "Cancelar";
    twoButtonConfirmed = () => {
      twoButtonTitle.value = "";
      twoButtonMessage.value = "";
      twoButtonAcceptButton.value = "";
      twoButtonCancelButton.value = "";
      changeCouponVisibilityConfirmed(item);
    };
    twoButtonCancelled = () => {
      twoButtonTitle.value = "";
      twoButtonMessage.value = "";
      twoButtonAcceptButton.value = "";
      twoButtonCancelButton.value = "";
    }
  };

  const changeCouponVisibilityConfirmed = async (item) => {
    loadingStore.setLoading(true);
    error.value = null;

    try {
      let response = await changeCouponVisibilityInAPI(item);

      if (response.status === 401) {
        const refreshed = await refreshToken();
        if (refreshed) {
          response = await changeCouponVisibilityInAPI();
        } else {
          loadingStore.setLoading(false);
          localStorage.removeItem('user_id');
          localStorage.removeItem('token');
          router.push('/');
          return;
        }
      }

      if (!response.ok) {
        showError("Se ha producido un error cambiando el estado del cupón.");
        return;
      }

      const data = await response.json();

      if (data.errors) {
        showError(data.message);
      } else {
        error.value = null;
      }
    } catch (exception) {
      showError(exception.message);
    } finally {
      getCoupons();
    }
  };

  const changeCouponVisibilityInAPI = async (item) => {
    return await fetch(process.env.VUE_APP_API_URL+"api/god/coupons/edit_visibility", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "vgod": process.env.VUE_APP_API_VGOD,
          "cp-token": process.env.VUE_APP_API_CP_TOKEN,
          "Authorization": "Bearer " + localStorage.getItem('token')
        },
        body: JSON.stringify({
          "user_id": localStorage.getItem('user_id'),
          "coupon": item.codigo.replace(" (Amigo)", ""),
          "visibility": item.activo ? 0 : 1 
        }),
      });
  };

  const showError = async (message) => {
    error.value = null;
    await nextTick(); // Espera a que Vue reaccione al cambio
    error.value = message;
  };

  async function addCoupon() {
    selectedCoupon.value = null;
    showCouponPopup.value = true;
  }

  const onCouponSaved = () => {
    showCouponPopup.value = false;
    getCoupons();
  };

  getCoupons();

  const couponPopupCancelled = () => {
    showCouponPopup.value = false;
  }

  const errorHappened = (message) => {
    showError(message);
  }
</script>

<style scoped>
.checkbox-filter {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13.5px;
  font-weight: 500;
  color: var(--text-color-dark);
  cursor: pointer;
  user-select: none;
  padding: 0 8px;
}

.text-subtle {
  font-size: 12px;
  color: var(--text-color-light);
}

.actions-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.big {
  width: 25%;
}

.small {
  width: 15%;
}

@media (max-width: 768px) {
  #selector_container > * {
    flex: none !important;
    width: 100%;
  }

  .actions-cell {
    flex-wrap: wrap;
    gap: 6px;
  }
}
</style>
